import { Controller } from "@hotwired/stimulus"

const FILE_TYPE_ICONS = {
  "pdf": "📄",
  "doc": "📝", "docx": "📝",
  "xls": "📊", "xlsx": "📊", "csv": "📊",
  "ppt": "📎", "pptx": "📎",
  "zip": "🗜️", "rar": "🗜️", "7z": "🗜️", "tar": "🗜️", "gz": "🗜️",
  "txt": "📃", "md": "📃", "log": "📃",
  "json": "{ }", "xml": "📋", "yml": "📋", "yaml": "📋",
  "mp4": "🎬", "mov": "🎬", "avi": "🎬", "webm": "🎬",
  "mp3": "🎵", "wav": "🎵", "ogg": "🎵",
}

function getFileIcon(filename) {
  const ext = filename.split(".").pop().toLowerCase()
  return FILE_TYPE_ICONS[ext] || "📎"
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default class extends Controller {
  static targets = [
    "writeTab", "previewTab", "writeBtn", "previewBtn",
    "body", "preview",
    "fileInput", "fileCount", "filePreviewArea", "filePreviewList"
  ]

  connect() {
    this.selectedFiles = []
  }

  showWrite() {
    this.writeTabTarget.classList.remove("hidden")
    this.previewTabTarget.classList.add("hidden")

    this.writeBtnTarget.classList.add("bg-white", "dark:bg-gray-700", "text-gray-700", "dark:text-gray-200", "shadow-sm", "border", "border-gray-200", "dark:border-gray-600")
    this.writeBtnTarget.classList.remove("text-gray-500", "dark:text-gray-400")

    this.previewBtnTarget.classList.remove("bg-white", "dark:bg-gray-700", "text-gray-700", "dark:text-gray-200", "shadow-sm", "border", "border-gray-200", "dark:border-gray-600")
    this.previewBtnTarget.classList.add("text-gray-500", "dark:text-gray-400")
  }

  showPreview() {
    this.writeTabTarget.classList.add("hidden")
    this.previewTabTarget.classList.remove("hidden")

    this.previewBtnTarget.classList.add("bg-white", "dark:bg-gray-700", "text-gray-700", "dark:text-gray-200", "shadow-sm", "border", "border-gray-200", "dark:border-gray-600")
    this.previewBtnTarget.classList.remove("text-gray-500", "dark:text-gray-400")

    this.writeBtnTarget.classList.remove("bg-white", "dark:bg-gray-700", "text-gray-700", "dark:text-gray-200", "shadow-sm", "border", "border-gray-200", "dark:border-gray-600")
    this.writeBtnTarget.classList.add("text-gray-500", "dark:text-gray-400")

    const text = this.bodyTarget.value.trim()
    if (text) {
      this.previewTarget.textContent = text
      this.previewTarget.classList.remove("italic", "text-gray-400")
    } else {
      this.previewTarget.innerHTML = '<span class="text-gray-400 italic">Nothing to preview</span>'
    }
  }

  previewFiles() {
    const newFiles = Array.from(this.fileInputTarget.files)
    this.selectedFiles.push(...newFiles)
    this._syncFileInput()
    this._renderFilePreviews()
  }

  removeFile(event) {
    event.preventDefault()
    const index = parseInt(event.currentTarget.dataset.index, 10)
    this.selectedFiles.splice(index, 1)
    this._syncFileInput()
    this._renderFilePreviews()
  }

  _syncFileInput() {
    const dt = new DataTransfer()
    this.selectedFiles.forEach(file => dt.items.add(file))
    this.fileInputTarget.files = dt.files
  }

  _renderFilePreviews() {
    const list = this.filePreviewListTarget
    const area = this.filePreviewAreaTarget

    list.innerHTML = ""

    if (this.selectedFiles.length === 0) {
      area.classList.add("hidden")
      this.fileCountTarget.textContent = ""
      return
    }

    area.classList.remove("hidden")
    this.fileCountTarget.textContent = `${this.selectedFiles.length} file${this.selectedFiles.length === 1 ? "" : "s"} selected`

    this.selectedFiles.forEach((file, index) => {
      if (file.type.startsWith("image/")) {
        this._renderImagePreview(file, index, list)
      } else {
        this._renderFileCard(file, index, list)
      }
    })
  }

  _createRemoveBtn(index) {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.dataset.index = index
    btn.dataset.action = "comment-form#removeFile"
    btn.className = "absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center text-xs leading-none shadow-sm transition-colors cursor-pointer"
    btn.innerHTML = "×"
    return btn
  }

  _renderImagePreview(file, index, container) {
    const reader = new FileReader()
    reader.onload = (e) => {
      const wrapper = document.createElement("div")
      wrapper.className = "relative group/thumb"

      const img = document.createElement("img")
      img.src = e.target.result
      img.className = "w-20 h-20 object-cover rounded-lg border border-gray-200 dark:border-gray-700"
      img.alt = file.name

      const nameTag = document.createElement("span")
      nameTag.className = "block text-[10px] text-gray-400 dark:text-gray-500 truncate w-20 mt-0.5"
      nameTag.textContent = file.name

      wrapper.appendChild(img)
      wrapper.appendChild(this._createRemoveBtn(index))
      wrapper.appendChild(nameTag)
      container.appendChild(wrapper)
    }
    reader.readAsDataURL(file)
  }

  _renderFileCard(file, index, container) {
    const wrapper = document.createElement("div")
    wrapper.className = "relative flex items-center gap-2 px-2.5 py-1.5 pr-7 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"

    const icon = document.createElement("span")
    icon.className = "text-base shrink-0"
    icon.textContent = getFileIcon(file.name)

    const info = document.createElement("div")
    info.className = "min-w-0 flex-1"

    const name = document.createElement("span")
    name.className = "block text-xs font-medium text-gray-700 dark:text-gray-300 truncate max-w-[140px]"
    name.textContent = file.name

    const size = document.createElement("span")
    size.className = "block text-[10px] text-gray-400 dark:text-gray-500"
    size.textContent = formatFileSize(file.size)

    info.appendChild(name)
    info.appendChild(size)
    wrapper.appendChild(icon)
    wrapper.appendChild(info)
    wrapper.appendChild(this._createRemoveBtn(index))
    container.appendChild(wrapper)
  }
}
